/**
 * CategoryHint (P3, Feb 2026)
 *
 * Inline-Tooltip „Wie wird das gebucht?" für SB-Belege/Rechnungs-Tabellen.
 * Zeigt Kategorie-Label, zugewiesenes Konto (SKR03/SKR04/etc.) und USt-Schlüssel.
 *
 * Verwendung:
 *   <CategoryHint clientId={cid} category="office" label="Bürobedarf" advisorToken={token} />
 */
import React, { useState, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { getAccountMapping } from '@/utils/accountMappingCache';

const TAX_KEY_LABELS = {
  VSt19: '19% Vorsteuer',
  VSt7: '7% Vorsteuer',
  VSt0: '0% (steuerfrei / EU-Reverse-Charge)',
  USt19: '19% Umsatzsteuer',
  USt7: '7% Umsatzsteuer',
  USt0: '0% (steuerfrei)',
  UST20: '20% Umsatzsteuer (AT)',
  UST10: '10% Umsatzsteuer (AT)',
  UST13: '13% Umsatzsteuer (AT)',
  VST20: '20% Vorsteuer (AT)',
  VST10: '10% Vorsteuer (AT)',
  VST13: '13% Vorsteuer (AT)',
};

export default function CategoryHint({ clientId, category, label, advisorToken }) {
  const [mapping, setMapping] = useState(null);
  useEffect(() => {
    if (!clientId || !category || !advisorToken) return;
    let alive = true;
    getAccountMapping(clientId, advisorToken)
      .then(d => { if (alive) setMapping(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [clientId, category, advisorToken]);

  const displayLabel = label || category || '—';
  if (!category) return <span>{displayLabel}</span>;

  // Mapping struktur: { chart_template, country, category_to_account: {label: {account, tax_key}} }
  // Keys können sowohl Frontend-Keys ('office') als auch Display-Labels ('Bürobedarf') sein
  // (Backend AI-Auto-Mapping nutzt teils Labels). Beide probieren.
  const entry = mapping?.category_to_account?.[category]
              ?? mapping?.category_to_account?.[label];
  const code = typeof entry === 'string'
    ? entry
    : (entry && typeof entry === 'object' ? entry.account : null);
  const taxKey = (entry && typeof entry === 'object') ? entry.tax_key : null;
  const tplId = mapping?.chart_template;
  const country = mapping?.country;

  let tooltip = 'Wie wird das gebucht?\n';
  tooltip += `Kategorie: ${displayLabel}\n`;
  if (code) {
    tooltip += `${(tplId || 'KONTO').toUpperCase()}: ${code}`;
    if (taxKey) tooltip += ` · ${TAX_KEY_LABELS[taxKey] || taxKey}`;
  } else {
    tooltip += `${(tplId || 'Konto').toUpperCase()}: noch nicht zugeordnet`;
  }
  if (country) tooltip += `\nLand: ${country}`;
  tooltip += '\n\nMapping bearbeiten: Tab „Kontenrahmen"';

  return (
    <span className="inline-flex items-center gap-1" title={tooltip}
      data-testid={`category-hint-${category}`}>
      <span>{displayLabel}</span>
      <HelpCircle className="w-3 h-3 text-slate-300 hover:text-slate-500 cursor-help shrink-0" />
    </span>
  );
}
